import { useCallback, useSyncExternalStore } from 'react';
import { useAuth } from '../contexts/AuthContext.jsx';

/**
 * useNextDesign — the new design (crm-kit/next) of the admin interface, same contract as Turbo.
 *
 * The choice is kept per user in localStorage (survives logout — see AuthContext) and shared live
 * between every hook instance (shell, tour, promo, related settings…).
 * Default: ON — the new design is the admin interface; "חזרה לעיצוב הקודם" in the personal menu turns it off.
 * The external portals (/rimush, /gabbai, /mishtameret, /exam) never read it.
 */
export const NEXT_DESIGN_STORAGE_PREFIX = 'bchinot-ui-next';
const LS_KEY = (userId) => `${NEXT_DESIGN_STORAGE_PREFIX}:${userId || 'anon'}`;
const EVENT = 'bchinot-ui-next:change';
const DEFAULTS = { uiNextDesign: true, uiThemeMode: 'system', uiThemeStyle: 'modern' };

const cache = new Map();
function read(userId) {
  const key = LS_KEY(userId);
  if (cache.has(key)) return cache.get(key);
  let value = DEFAULTS;
  try {
    const raw = localStorage.getItem(key);
    if (raw) value = { ...DEFAULTS, ...JSON.parse(raw) };
  } catch {
    /* storage blocked */
  }
  cache.set(key, value);
  return value;
}
function write(userId, patch) {
  const key = LS_KEY(userId);
  const next = { ...read(userId), ...patch };
  cache.set(key, next);
  try {
    localStorage.setItem(key, JSON.stringify(next));
  } catch {
    /* storage blocked */
  }
  window.dispatchEvent(new Event(EVENT));
}
function subscribe(cb) {
  const onStorage = (e) => {
    if (e.key && e.key.startsWith(NEXT_DESIGN_STORAGE_PREFIX)) {
      cache.clear();
      cb();
    }
  };
  window.addEventListener(EVENT, cb);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(EVENT, cb);
    window.removeEventListener('storage', onStorage);
  };
}

export const userKeyOf = (user) => user?._id || user?.id || user?.username || 'anon';

export default function useNextDesign() {
  const { user } = useAuth();
  const uid = userKeyOf(user);
  const choice = useSyncExternalStore(subscribe, () => read(uid), () => read(uid));
  const save = useCallback((patch) => write(uid, patch), [uid]);

  return {
    allowed: true,
    enabled: !!choice.uiNextDesign,
    mode: choice.uiThemeMode || 'system',
    style: choice.uiThemeStyle || 'modern',
    userKey: uid,
    setEnabled: (on) => save({ uiNextDesign: !!on }),
    setMode: (m) => save({ uiThemeMode: m }),
    setStyle: (s) => save({ uiThemeStyle: s }),
  };
}
